import Vue from 'vue'
import VueRouter, { RouteConfig } from 'vue-router'
import { Component } from 'vue-router/types/router'
import store from '@/store/index'
import { Services } from '@/utils/services.enum'
import { Roles, profilePermissions } from '@/plugins/permissions'

Vue.use(VueRouter)

function load(view: string): Component {
  return () =>
    import(/* webpackChunkName: "view-[request]" */ `@/views/${view}.vue`)
}

function canPass(service: string): boolean {
  if (!store.getters.profile) {
    return true
  } else if (!store.getters.profile.services) {
    return true
  } else return store.getters.profile.services.includes(service)
}

const routes: Array<RouteConfig> = [
  {
    path: '/auth',
    component: load('Auth/Auth'),
    children: [
      {
        path: 'login',
        name: 'login',
        component: load('Auth/Login'),
      },
      {
        path: 'verify',
        name: 'verify',
        component: load('Auth/Verify'),
        beforeEnter: (to, from, next) => {
          if (!from || from.name !== 'login') next({ name: 'login' })
          else next()
        },
      },
    ],
  },
  {
    path: '/',
    name: 'root',
    component: load('Home'),
    redirect: { name: 'dashboard' },
    meta: {
      requiresAuth: true,
    },
    children: [
      {
        path: 'notfound',
        name: 'notfound',
        component: load('NotFound'),
      },
      {
        path: 'home',
        name: 'dashboard',
        component: load('Dashboard'),
        beforeEnter: (to, from, next) => {
          if (
            (from.name === 'login' || from.name === 'verify' || !from.name) &&
            canPass(Services.Topup)
          ) {
            if (localStorage.getItem('topupScreen') === 'true') {
              next({ name: 'topup.create' })
            } else if (localStorage.getItem('remittanceScreen') === 'true') {
              next({ name: 'remittances-list' })
            } else next()
          } else next()
        },
      },
      {
        path: 'account',
        name: 'account',
        component: load('Account'),
      },
      {
        path: 'nauta',
        name: 'nauta.nauta',
        redirect: { name: 'nauta.list' },
        component: load('Services/Nauta/Home'),
        beforeEnter: (to, from, next) => {
          if (
            store.getters.profile &&
            store.getters.profile.services &&
            (store.getters.profile.parent ||
              !store.getters.profile.services.includes(Services.Nauta))
          ) {
            to.meta.noPass = true
          } else to.meta.noPass = false
          next()
        },
        children: [
          {
            path: 'create',
            name: 'nauta.create',
            component: load('Services/Nauta/Create'),
            meta: {
              roles: ['ADMIN', 'AGENT'],
            },
          },
          {
            path: 'list',
            name: 'nauta.list',
            component: load('Services/Nauta/List'),
            meta: {
              roles: ['ADMIN', 'AGENT'],
            },
          },
        ],
      },
      {
        path: 'rental-car',
        name: 'rental.rental_car',
        component: load('Services/RentalCar/Index'),
        beforeEnter: (to, _, next) => {
          if (!store.getters.profile?.services.includes(Services.RentalCar)) {
            to.meta.noPass = true
          } else to.meta.noPass = false
          next()
        },
        children: [
          {
            path: 'contracts',
            name: 'rental.contracts',
            component: load('Services/RentalCar/CarContracts'),
          },
          {
            path: 'shared-contracts',
            name: 'rental.shared_contracts',
            component: load('Services/RentalCar/CarSharedContracts'),
          },
          {
            path: 'shared-prices',
            name: 'rental.shared_prices',
            component: load('Services/RentalCar/CarContractSharedPrice'),
          },
          {
            path: 'contract-prices',
            name: 'rental.contract_prices',
            component: load('Services/RentalCar/CarContractPrice'),
          },
          {
            path: 'bookings',
            name: 'rental.bookings',
            component: load('Services/RentalCar/CarBookings'),
          },
          {
            path: 'agencies',
            name: 'rental.agencies',
            component: load('Services/RentalCar/CarAgencies'),
          },
          {
            path: 'clients',
            name: 'rental.clients',
            component: load('Services/RentalCar/CarClients'),
          },
          {
            path: 'categories',
            name: 'rental.categories',
            component: load('Services/RentalCar/CarCategories'),
          },
          {
            path: 'offices',
            name: 'rental.offices',
            component: load('Services/RentalCar/CarOffices'),
          },
          {
            path: 'seasons',
            name: 'rental.seasons',
            component: load('Services/RentalCar/CarSeasons'),
          },
          {
            path: 'price-config',
            name: 'rental.price_config',
            component: load('Services/RentalCar/CarPriceConfig'),
            meta: {
              roles: [Roles.ADMIN],
            },
          },
        ],
      },
      {
        path: 'topup',
        name: 'topup.topup',
        component: load('Services/TopUps/Home'),
        beforeEnter: (to, from, next) => {
          if (
            store.getters.profile &&
            store.getters.profile.services &&
            !store.getters.profile.services.includes('TOP-UP')
          ) {
            to.meta.noPass = true
          } else to.meta.noPass = false
          next()
        },
        children: [
          {
            path: 'create',
            name: 'topup.create',
            component: load('Services/TopUps/CreateTopUp'),
            meta: {
              roles: ['ADMIN', 'AGENT', 'EMPLOYEE'],
            },
          },
          {
            path: 'list',
            name: 'topup.list',
            component: load('Services/TopUps/TopUpList'),
            meta: {
              roles: ['ADMIN', 'AGENT', 'INTEGRATION', 'EMPLOYEE'],
            },
          },
          {
            path: 'agents',
            name: 'topup.agent-prices',
            component: load('Services/TopUps/Agents'),
            meta: {
              roles: ['ADMIN', 'AGENT'],
            },
          },
          {
            path: 'credit-transfer',
            name: 'topup.credit-transfer',
            component: load('Services/TopUps/CreditTransfer'),
            meta: {
              roles: ['ADMIN', 'AGENT'],
            },
          },
          {
            path: 'credit',
            name: 'topup.credit',
            component: load('Services/TopUps/Credit'),
            meta: {
              roles: ['ADMIN'],
            },
          },
          {
            path: 'reports',
            name: 'topup.reports',
            component: load('Services/TopUps/Reports'),
            meta: {
              roles: ['ADMIN', 'AGENT'],
            },
          },
          {
            path: 'products',
            name: 'topup.products',
            component: load('Services/TopUps/Products'),
            meta: {
              requiresMaikel: true,
              roles: ['ADMIN'],
            },
          },
          {
            path: 'deals',
            name: 'topup.deals',
            component: load('Services/TopUps/Deals'),
            meta: {
              roles: ['ADMIN'],
            },
          },
          {
            path: 'provider-balance',
            name: 'topup.provider-balance',
            component: load('Services/TopUps/Providers'),
            meta: {
              roles: ['ADMIN'],
            },
          },
          {
            path: 'provider-prices',
            name: 'topup.provider-prices',
            component: load('Services/TopUps/PriceSettings'),
            meta: {
              roles: ['ADMIN'],
            },
          },
          {
            path: 'integrations',
            name: 'topup.integrations',
            component: load('Services/TopUps/Integrations'),
            meta: {
              roles: ['ADMIN'],
            },
          },
          {
            path: 'platform-price',
            name: 'topup.platform-price',
            component: load('Services/TopUps/PlatformPrice'),
            meta: {
              roles: ['ADMIN'],
            },
          },
          {
            path: 'balance-trasnfer',
            name: 'topup.balance_transfer',
            component: load('Services/TopUps/BalanceTransfer'),
            meta: {
              roles: [Roles.ADMIN, Roles.AGENT],
            },
          },
          {
            path: 'start_deal',
            name: 'topup.start_deal',
            component: load('Services/TopUps/NuclearLaunch'),
            meta: {
              roles: ['ADMIN'],
              requiresMaikel: true,
            },
          },
          {
            path: 'transactions',
            component: load('Services/TopUps/Transactions'),
            children: [
              {
                path: 'credit',
                name: 'topup.credit_transactions',
                component: load('Services/TopUps/Transactions/Credit'),
              },
              {
                path: 'balance',
                name: 'topup.balance_transactions',
                component: load('Services/TopUps/Transactions/Balance'),
              },
              {
                path: 'provider-credit',
                name: 'topup.provider_credit_transactions',
                component: load('Services/TopUps/Transactions/ProviderCredit'),
                meta: {
                  roles: [Roles.ADMIN],
                },
              },
              {
                path: 'agency',
                name: 'topup.agency_transactions',
                component: load('Services/TopUps/Transactions/Agency'),
                meta: {
                  roles: [Roles.ADMIN],
                },
              },
            ],
          },
        ],
      },
      {
        path: 'remittances',
        name: 'remittances',
        component: load('Services/Remittances/Home'),
        beforeEnter: (to, from, next) => {
          if (
            store.getters.profile &&
            store.getters.profile.services &&
            !store.getters.profile.services.includes(Services.Remittance)
          ) {
            to.meta.noPass = true
          } else to.meta.noPass = false
          next()
        },
        children: [
          {
            path: 'create',
            name: 'create-remittance',
            component: load('Services/Remittances/CreateRemittance'),
            meta: {
              roles: ['ADMIN', 'EMPLOYEE', 'ACCOUNTANT'],
            },
          },
          {
            path: 'create-mlc',
            name: 'create-remittance-mlc',
            component: load('Services/Remittances/CreateRemittanceMLC'),
            meta: {
              roles: ['ADMIN', 'EMPLOYEE', 'ACCOUNTANT'],
            },
          },
          {
            path: 'create-mlx',
            name: 'create-remittance-mlx',
            component: load('Services/Remittances/CreateRemittanceMLX'),
            meta: {
              roles: ['ADMIN', 'EMPLOYEE', 'ACCOUNTANT'],
            },
          },
          {
            path: 'create-ust',
            name: 'create-remittance-ust',
            component: load('Services/Remittances/CreateRemittanceUST'),
            meta: {
              roles: ['ADMIN', 'EMPLOYEE', 'ACCOUNTANT'],
            },
          },
          {
            path: 'consult-remittance-price',
            name: 'consult-remittance-price',
            component: load('Services/Remittances/ConsultRemittancePrice'),
            meta: {
              roles: ['ADMIN', 'EMPLOYEE', 'ACCOUNTANT'],
            },
          },
          {
            path: 'list',
            name: 'remittances-list',
            component: load('Services/Remittances/RemittancesList'),
          },
          {
            path: 'remittance-invoices',
            name: 'remittance-invoices',
            component: load('Services/Remittances/Invoices'),
            meta: {
              roles: ['ADMIN', Roles.EMPLOYEE],
            },
          },
          {
            path: 'tariff',
            name: 'tariff',
            component: load('Services/Remittances/Tariff'),
            meta: {
              roles: ['ADMIN'],
            },
          },
          {
            path: 'messengers',
            name: 'messengers',
            component: load('Services/Remittances/Messengers'),
            meta: {
              roles: ['ADMIN', 'AGENT'],
            },
          },
          {
            path: 'wholesaler',
            name: 'remittance.wholesaler',
            component: load('Services/Remittances/Wholesaler'),
            meta: {
              roles: ['ADMIN', 'WHOLESALER'],
            },
          },
          {
            path: 'auto-assign',
            name: 'remittance.auto_assign',
            component: load('Services/Remittances/AutoAssign'),
            meta: {
              roles: ['ADMIN'],
            },
          },
          {
            path: 'auto-up',
            name: 'remittance.auto_up',
            component: load('Services/Remittances/AutoUp'),
            meta: {
              roles: [Roles.ADMIN],
            },
          },
          {
            path: 'currency-fee',
            name: 'remittance.currency-fee',
            component: load('Services/Remittances/RemittanceFee'),
            meta: {
              roles: ['ADMIN'],
            },
          },
          {
            path: 'platform-price',
            name: 'remittance.platform-price',
            component: load('Services/Remittances/PlatformPrice'),
            meta: {
              roles: ['ADMIN'],
            },
          },
          {
            path: 'reports',
            name: 'remittance.reports',
            component: load('Services/Remittances/Reports'),
            meta: {
              roles: ['ADMIN'],
            },
          },
          {
            path: 'balances',
            component: load('Services/Remittances/Balances/index'),
            meta: {
              roles: [Roles.ADMIN],
            },
            children: [
              {
                path: 'balances',
                name: 'remittance.balances',
                component: load('Services/Remittances/Balances/Balances'),
              },
              {
                path: 'balances-self',
                name: 'remittance.balances_self',
                component: load('Services/Remittances/Balances/BalancesSelf'),
              },
              {
                path: 'balances-transactions',
                name: 'remittance.balances_transactions',
                component: load(
                  'Services/Remittances/Balances/BalancesTransactions',
                ),
              },
              {
                path: 'balances-self-transactions',
                name: 'remittance.balances_self_transactions',
                component: load(
                  'Services/Remittances/Balances/BalancesSelfTransactions',
                ),
              },
            ],
          },
          {
            path: 'transactions',
            component: load('Services/Remittances/Transactions'),
            children: [
              {
                path: 'balance',
                name: 'remittance.balance_transactions',
                component: load('Services/Remittances/Transactions/Balance'),
              },
              {
                path: 'agency',
                name: 'remittance.agency_transactions',
                component: load('Services/Remittances/Transactions/Agency'),
                meta: {
                  roles: [Roles.ADMIN],
                },
              },
            ],
          },
        ],
      },
      {
        path: 'messages',
        name: 'messages',
        component: load('Services/Sms/Home'),
        beforeEnter: (to, from, next) => {
          if (
            store.getters.profile &&
            store.getters.profile.services &&
            (store.getters.profile.parent ||
              !store.getters.profile.services.includes(Services.Sms))
          ) {
            to.meta.noPass = true
          } else to.meta.noPass = false
          next()
        },
        redirect: { name: 'sms.list' },
        children: [
          {
            path: 'send',
            name: 'sms.send',
            component: load('Services/Sms/Send'),
            meta: {
              roles: ['ADMIN', 'AGENT'],
            },
          },
          {
            path: 'list',
            name: 'sms.list',
            component: load('Services/Sms/List'),
            meta: {
              roles: ['ADMIN', 'AGENT'],
            },
          },
          {
            path: 'clients',
            name: 'sms.clients',
            component: load('Services/Sms/Clients'),
            meta: {
              roles: ['ADMIN', 'AGENT'],
            },
          },
          {
            path: 'tags',
            name: 'sms.tags',
            component: load('Services/Sms/Tags'),
            meta: {
              roles: ['ADMIN', 'AGENT'],
            },
          },
          {
            path: 'products',
            name: 'sms.products',
            component: load('Services/Sms/Products'),
            meta: {
              roles: ['ADMIN'],
            },
          },
        ],
      },
      {
        path: 'agency',
        name: 'agency',
        component: load('Agency'),
        children: [
          {
            path: 'announcements',
            name: 'agency-announcements',
            component: load('Agency/Announcements'),
            meta: {
              roles: ['ADMIN'],
            },
          },
          {
            path: 'clients',
            name: 'agency-clients',
            component: load('Agency/Clients'),
            meta: {
              roles: ['ADMIN', 'EMPLOYEE'],
            },
          },
          {
            path: 'daughters',
            name: 'agency-daughters',
            component: load('Agency/Daughters'),
            meta: {
              roles: ['ADMIN', Roles.EMPLOYEE],
            },
          },
          {
            path: 'users',
            name: 'agency-users',
            component: load('Agency/Users'),
            meta: {
              roles: ['ADMIN', 'AGENT', Roles.ACCOUNTANT],
            },
          },
          {
            path: 'transactions',
            name: 'agency.transactions',
            component: load('Agency/Transactions'),
            meta: {
              roles: ['ADMIN', 'AGENT', 'INTEGRATION', Roles.ACCOUNTANT],
            },
          },
          {
            path: 'wholesaler-transactions',
            name: 'agency.wholesaler-transactions',
            component: load('Agency/BillTransactions'),
            meta: {
              roles: ['ADMIN', 'WHOLESALER', Roles.EMPLOYEE],
            },
          },
          {
            path: 'payment_list',
            name: 'agency.payment_list',
            component: load('Agency/PaymentList'),
            meta: {
              roles: ['ADMIN', 'AGENT', 'ACCOUNTANT', Roles.EMPLOYEE],
            },
            beforeEnter: (to, from, next) => {
              const proPerm = profilePermissions()
              if (
                store.getters.profile &&
                (store.getters.profile.parent ||
                  (store.getters.profile.role === Roles.EMPLOYEE &&
                    !proPerm.canSeePayments))
              ) {
                to.meta.noPass = true
              } else to.meta.noPass = false
              next()
            },
          },
          {
            path: 'invoices',
            name: 'agency.invoices',
            component: load('Agency/Invoices'),
            meta: {
              roles: ['ADMIN', 'ACCOUNTANT'],
            },
          },
        ],
      },
      {
        path: 'transactions',
        component: load('Transactions'),
        children: [
          {
            path: 'balance',
            name: 'transactions.balance_transactions',
            component: load('Transactions/Balance'),
            meta: {
              roles: ['ADMIN', 'AGENT', 'INTEGRATION'],
            },
          },
          {
            path: 'funds',
            name: 'transactions.funds_transactions',
            component: load('Transactions/Fund'),
            meta: {
              roles: ['ADMIN', 'AGENT'],
            },
          },
          {
            path: 'agency',
            name: 'transactions.agency_transactions',
            component: load('Transactions/AgencyBalance'),
            meta: {
              roles: ['ADMIN', 'WHOLESALER', Roles.EMPLOYEE],
            },
          },
        ],
      },
      {
        path: 'settings',
        name: '/settings',
        component: load('Settings/Settings'),
        children: [
          {
            name: 'transfer-balance',
            path: 'transfer-balance',
            component: load('Settings/TransferBalance'),
            meta: {
              roles: [
                Roles.ADMIN,
                Roles.AGENT,
                Roles.ACCOUNTANT,
                Roles.EMPLOYEE,
              ],
            },
          },
          {
            name: 'preferences',
            path: 'preferences',
            component: load('Settings/Preferences'),
          },
        ],
      },
    ],
  },
  {
    path: '/maintenance',
    name: 'maintenance',
    component: load('Maintenance'),
  },
  {
    path: '/signature/:uuid',
    name: 'signature',
    component: load('Services/Remittances/Signature'),
  },
  {
    path: '*',
    redirect: { name: 'dashboard' },
  },
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
})

router.beforeResolve((to, from, next) => {
  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!store.getters.isLoggedIn) {
      next({ name: 'login' })
    } else if (to.matched.some(record => record.meta.noPass)) {
      next({ name: 'notfound' })
    } else if (to.matched.some(record => record.meta.requiresMaikel)) {
      if (
        store.getters.profile.is_superuser ||
        store.getters.profile.username === 'maikel'
      ) {
        next()
      } else {
        next({ name: 'notfound' })
      }
    } else if (to.matched.some(record => !!record.meta.roles)) {
      if (
        to.matched.some(
          route =>
            route.meta.roles &&
            !route.meta.roles.includes(store.getters.profile.role),
        )
      ) {
        next({ name: 'notfound' })
      } else next()
    } else next()
  } else next()
})

export default router
